import React, { useEffect, useMemo, useState } from 'react';
import Status from './Columns/Status';
import AddPortfolio from './AddPortfolio';
import { getPortfolioColumns } from './Header';
import { STATUS } from '../../Constants/Constants';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { FOLDER_REQUESTS_ENDPOINT, PORTFOLIO_ENDPOINT } from '../../urls';
import { DataTable, SearchBar, Select, showToast, StyleProvider } from '@attentive-platform/stem-ui';
import { useSearchParams } from 'react-router-dom';
import { getFolderRequestsColumns } from './FolderRequestsColumns';
import PortfolioRequestBtns from './PortfolioRequestBtns';

const Portfolio = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [portfolioData, setPortfolioData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showAddPortfolio, setShowAddPortfolio] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const folderId = searchParams?.get('folderId');
  const portfolioId = searchParams?.get('portfolioId');

  const getPortFolioData = async () => {
    setIsFetching(true);
    const { data } = await fetchApiData({
      endpoint: PORTFOLIO_ENDPOINT,
      apiRequest: 'GET',
      params: {
        page: page + 1,
        page_size: pageSize,
        ...(statusFilter && { status: statusFilter }),
        ...(searchText && { portfolio_name: searchText })
      }
    });
    setIsFetching(false);
    setPortfolioData(data);
  };

  const handlePaginationReset = () => {
    setPage(0);
    setPageSize(10);
    setSearchText('');
    setStatusFilter(null);
    setSelectedRows([]);
  };

  const handleFolderClick = (folderId, portfolioId) => {
    setSearchParams({ folderId, portfolioId });
  };

  const fetchFolderRequests = async () => {
    try {
      if (!folderId) return;

      handlePaginationReset();
      setIsFetching(true);
      const url = interpolate(FOLDER_REQUESTS_ENDPOINT, [folderId]);
      const { data } = await fetchApiData({
        endpoint: url,
        apiRequest: 'GET',
        params: {
          page: page + 1,
          page_size: 20,
          folder_id: folderId,
          is_folder: false,
          q: searchText
        }
      });
      setPortfolioData(data);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setIsFetching(false);
    }
  };

  const columns = folderId
    ? getFolderRequestsColumns(selectedRows, setSelectedRows)
    : getPortfolioColumns(getPortFolioData, setPortfolioData, handleFolderClick);

  const handleSearchTextChange = value => {
    setPage(0);
    setPageSize(10);
    setSearchText(value);
  };

  const handleStatusChange = status => {
    handlePaginationReset();
    setStatusFilter(status);
  };

  const handleChangePage = page => {
    setPage(page);
  };

  const handleChangePageSize = pageSize => {
    setPageSize(pageSize);
  };

  const paginationProps = useMemo(
    () => ({
      handleChangePage,
      handleChangePageSize,
      page,
      pageSize,
      hidePagination: !isFetching && (portfolioData || []).length === 0
    }),
    [page, pageSize, isFetching, portfolioData]
  );

  const handleBackToPortfolio = () => {
    setSearchParams({});
    setSelectedRows([]);
  };

  useEffect(() => {
    if (folderId) {
      fetchFolderRequests();
    } else {
      getPortFolioData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, statusFilter, page, pageSize, folderId, searchParams]);

  return (
    <div className='m-4'>
      {folderId && (
        <div className='mb-2 flex items-center gap-2'>
          <span
            className='text-sm font-medium text-[#222] cursor-pointer hover:underline'
            onClick={handleBackToPortfolio}>
            Portfolio
          </span>
          {portfolioData?.folder_name && (
            <>
              <span className='text-sm font-medium text-[#222]'>/</span>
              <span className='text-sm font-medium text-green-600'>{portfolioData.folder_name}</span>
            </>
          )}
        </div>
      )}
      <div className='flex justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <SearchBar
            color='secondary'
            placeholder='Search...'
            onChangeFn={value => handleSearchTextChange(value)}
            minChars={1}
          />
          {folderId && (
            <PortfolioRequestBtns
              selectedRows={selectedRows}
              portfolioId={portfolioId}
              refetchData={fetchFolderRequests}
              isFetching={isFetching}
            />
          )}
        </div>
        {!folderId && (
          <div className='flex w-56 items-center gap-2'>
            <span className='text-md font-medium mr-2'>Filters:</span>
            <Select
              allowClear
              color='primary'
              items={Object.entries(STATUS).map(([key, value]) => {
                return {
                  label: value,
                  value: Number(key)
                };
              })}
              renderOption={(...args) => {
                return <Status status={args[0].value} />;
              }}
              onChange={handleStatusChange}
              placeholder='Status'
              variant='std-filled'
              className='h-[2.8125rem]'
            />
          </div>
        )}
      </div>
      <div className='flex justify-between items-center pt-4'>
        <StyleProvider>
          <DataTable
            rowProps={{ isSelectable: () => false }}
            allowBulkSelect={false}
            isLoading={isFetching}
            data={portfolioData?.results || []}
            columns={columns}
            options={{
              showFooter: false,
              stickyHeader: true
            }}
            classes={{
              'csm-container': 'scrollbar'
            }}
            {...(!folderId && {
              createRowProps: {
                showCreateRow: true,
                createRowBtnText: 'Add Portfolio',
                handleClickCreateBtn: () => setShowAddPortfolio(true)
              }
            })}
            bodyScrollProps={{ bodyScrollHeight: '36rem' }}
            paginationProps={paginationProps}
            totalCountProps={{
              showTotalCount: true,
              totalRowCount: portfolioData?.count
            }}
          />
        </StyleProvider>
      </div>
      <AddPortfolio show={showAddPortfolio} setShow={setShowAddPortfolio} getPortFolioData={getPortFolioData} />
    </div>
  );
};
export default Portfolio;
